import { Edge } from 'reactflow';

import { toast } from '@raystack/apsara';
import axios, { AxiosError } from 'axios';
import * as _ from 'lodash';
import queryString from 'query-string';
import { mutate } from 'swr';

import api from '@src/api';
import {
  V1Alpha1Component,
  V1Alpha1InferenceListResponseBody,
  V1Alpha1Input,
  V1Alpha1ResponseWorkflowGet,
  V1Alpha1Spec,
  V1Alpha1ValError
} from '@src/clients/dexter/api';
import { defaultNodeStyle } from '@src/containers/Workflow/Lab/LabStyles';
import {
  setEditInput,
  setSelectedBlock
} from '@src/redux/_reducers/blockReducer';
import {
  emptySelectedAOIS,
  setActiveJob,
  setActiveWorkflow,
  updateEstimatedCost,
  updateValidations
} from '@src/redux/_reducers/workFlowReducer';
import store from '@src/redux/store';

import { COLORMAP_TYPES, RESAMPLING_METHODS } from './constants/APP';
import {
  DISCRETISATION_TYPES,
  MODELS_ALLOWED_CHANGE_DETECTION,
  PARAMETER_MODELS
} from './constants/MODE_INFERENCE_DEFAULTS';
import {
  DEXTER_TOKEN_KEY,
  INPUT_FORMATS,
  REACTFLOW_ID_PREFIX_EDGE,
  VISUALISATION_TYPES
} from './constants/WORKFLOW_CONSTANTS';
import { h2RGdal } from './hexToRgb';

type bandwise_range = {
  min: number;
  max: number;
};

type bandwiseBucketItems = {
  min: number;
  max: number;
  color_code: string;
};

type bandwiseBucket = {
  items: bandwiseBucketItems[];
};

type continuous = {
  color_map_name: string;
  bandwise_range: bandwise_range[];
};

type discrete = {
  [key: string]: string;
};

type bucket = {
  bandwise: bandwiseBucket[];
};

type visualisation = {
  type: string;
  continuous: continuous;
  discrete: discrete;
  bucket: bucket;
};

type range = [number, number];

type DiscreteClasses = {
  color: string;
  name: string;
  value: string;
  range: null | range;
};

type discretization = {
  type: string;
  classes: DiscreteClasses[];
};
interface properties {
  date: string;
  visualisation?: visualisation;
  discretization?: discretization;
}

type ValError = {
  component_name: string;
  err: string;
  from: string;
  reason: string;
  to: string;
};

type blockSpec = {
  name?: string;
  title?: string;
  type?: string;
  inputs: V1Alpha1Input[];
  outputs: V1Alpha1Input[];
  version?: string;
};

export type V1Alpha1Output = {
  name: string;
  properties: properties;
  // optional properties frontend dependant
  isLog?: boolean;
};

interface block {
  spec: blockSpec;
}

// Clear all workflow related redux states
export const ClearWorkflowStates = () => {
  store.dispatch(setActiveWorkflow({}));
  store.dispatch(updateValidations({}));
  store.dispatch(setActiveJob({}));
  store.dispatch(emptySelectedAOIS([]));
  store.dispatch(setSelectedBlock({}));
  store.dispatch(setEditInput(false));
  store.dispatch(updateEstimatedCost(0));
};

// converts all input values to string, if there are any
const sanitiseInputs = (workflow: V1Alpha1ResponseWorkflowGet) => {
  const components = [
    ...(workflow.spec?.components.map((comp: V1Alpha1Component) => {
      const inputs = [
        ...(comp?.inputs?.map(input => {
          if (_.has(input, 'value')) {
            const value = input?.value?.toString() || '';
            return {
              ...input,
              value: value,
              name: input.name
            };
          }
          return input;
        }) || [])
      ];

      const component = { ...comp, inputs };

      return component;
    }) || [])
  ];

  return components;
};

// Submits workflow create Request
export async function CreateWorkflowV2(setLoader: (value: boolean) => void) {
  const state = store.getState();
  const activeWorkflow = state.workflow.activeWorkflow;
  const workflowObj: V1Alpha1ResponseWorkflowGet = {
    ..._.cloneDeep(activeWorkflow)
  };

  // convert input value to string
  const components = sanitiseInputs(workflowObj);

  setLoader(true);

  try {
    const result = await api.dexter.post(`/v1alpha1/workflows/`, {
      ...workflowObj,
      spec: {
        ...workflowObj.spec,
        components: [...components]
      }
    });

    const validations =
      (result.data.data.val_errors !== null &&
        result.data.data.val_errors.length > 0 &&
        result.data.data.val_errors) ||
      [];

    // check for validation errors
    if (validations.length > 0) {
      const validationDict = validations.reduce(
        (acc: { [x: string]: V1Alpha1ValError[] }, value: ValError) => {
          if (_.has(acc, value?.component_name)) {
            acc[value?.component_name] = [...acc[value?.component_name], value];
            return acc;
          }

          acc[value?.component_name] = [value];
          return acc;
        },
        {}
      );

      store.dispatch(updateValidations(validationDict));
    } else store.dispatch(updateValidations({}));

    // update activeWorkflow
    const workflowCopy: V1Alpha1ResponseWorkflowGet = {
      ...activeWorkflow,
      id: result.data.data.id
    };
    store.dispatch(setActiveWorkflow(workflowCopy));
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 409) {
        toast.error(axiosError?.response?.data?.error?.message, {
          description: 'Please try again with different name!'
        });
        return;
      }
      const message =
        axiosError?.response?.data?.error?.message ??
        'Oops! There was an issue creating workflow!';
      toast.error(message, { description: 'Please try again!' });
    }
  } finally {
    setLoader(false);
  }
}

// Submits workflow update Request
export async function UpdateWorkflowV2(setLoader: (value: boolean) => void) {
  const state = store.getState();
  const activeWorkflow = state.workflow
    .activeWorkflow as V1Alpha1ResponseWorkflowGet;

  const workflowObj: V1Alpha1ResponseWorkflowGet = {
    ..._.cloneDeep(activeWorkflow)
  };
  delete workflowObj.id;

  // convert input value to string
  const components = sanitiseInputs(workflowObj);

  setLoader(true);

  try {
    const result = await api.dexter.patch(
      `/v1alpha1/workflows/${activeWorkflow.id}`,
      {
        ...workflowObj,
        spec: {
          ...workflowObj.spec,
          components: [...components]
        }
      }
    );

    const estimated_token = _.has(result.data.data, DEXTER_TOKEN_KEY)
      ? result.data.data[`${DEXTER_TOKEN_KEY}`]
      : 0;
    // update tokens
    store.dispatch(updateEstimatedCost(estimated_token));

    const validations =
      result.data.data.val_errors !== null &&
      result.data.data.val_errors.length > 0
        ? result.data.data.val_errors
        : [];

    // check for validation errors
    if (validations.length > 0) {
      const validationDict = validations.reduce(
        (acc: { [x: string]: V1Alpha1ValError[] }, value: ValError) => {
          if (_.has(acc, value?.component_name)) {
            acc[value?.component_name] = [...acc[value?.component_name], value];
            return acc;
          }

          acc[value?.component_name] = [value];
          return acc;
        },
        {}
      );
      store.dispatch(updateValidations(validationDict));
    } else store.dispatch(updateValidations({}));
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 400) {
        toast.error('Oops! Could not update workflow!', {
          description: axiosError?.response?.data?.detail
        });
        return;
      }
    }
  } finally {
    setLoader(false);
  }
}

// Submits Job Create Request
export async function CreateJobs(
  setLoader: (value: boolean) => void,
  orgSlug: string,
  navigate: (value: string) => void
) {
  const state = store.getState();
  const activeWorkflow = state.workflow
    .activeWorkflow as V1Alpha1ResponseWorkflowGet;

  setLoader(true);
  try {
    const resp = await api.dexter.post(
      `/v1alpha1/workflows/${activeWorkflow.id}/jobs?project_id=${activeWorkflow.project_id}`
    );

    const job_id = resp?.data?.data?.id;
    const path = job_id
      ? `/${orgSlug}/workflows/${activeWorkflow.id}/jobs/${job_id}`
      : `/${orgSlug}/workflows/${activeWorkflow.id}/jobs`;
    mutate(`/v1alpha1/workflows/${activeWorkflow.id}/jobs`);
    navigate(path);
  } catch (error) {
    toast.error('Oops! Something went wrong!', {
      description:
        "We're currently experiencing some technical difficulties on our end, but we're working to resolve them! Please try again later."
    });
  } finally {
    setLoader(false);
  }
}

export const sanitizeAndSetActiveWorkflow = async (
  workflow: V1Alpha1ResponseWorkflowGet
) => {
  try {
    const estimated_token = _.has(workflow, DEXTER_TOKEN_KEY)
      ? workflow[`${DEXTER_TOKEN_KEY}`]
      : 0;

    store.dispatch(updateEstimatedCost(estimated_token));
    // clean null input attrs
    // santizes component to get rid of any key, with value null, inside the input
    const specComponents = workflow.spec?.components || [];
    const components = [
      ...(specComponents.map((comp: V1Alpha1Component) => {
        const inputs = [
          ...(comp?.inputs?.map(input => {
            const inputKeys = Object.keys(input) as (keyof V1Alpha1Input)[];
            inputKeys.map(key => {
              if (input[key] === null) {
                delete input[key];
              }
            });

            return input;
          }) || [])
        ];

        const component: V1Alpha1Component = { ...comp, inputs };

        return component;
      }) || [])
    ];

    store.dispatch(
      setActiveWorkflow({
        ...workflow,
        spec: {
          ...workflow.spec,
          components: [...components]
        }
      })
    );
  } catch (error) {
    console.error(error);
  }
};

/**
 *
 * @param {String} source
 * @param {String} sourceHandle
 * @param {String} target
 * @param {String} targetHandle
 * @returns {String} - ReactFlowEdge ID string
 */
export const createReactFlowEdgeId = (
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string
) => {
  const id = `${REACTFLOW_ID_PREFIX_EDGE}-${source}${sourceHandle}-${target}${targetHandle}`;

  return id;
};

/**
 *
 * @param {V1Alpha1Spec}  spec - Expects spec of type V1Alpha1Spec
 * @returns {Array} - nodes
 */
export const CreateCanvasNodes = async (spec: V1Alpha1Spec) => {
  const blocks = store.getState().blocks.blocks;

  // Function to fetch block data via API
  const fetchBlockData = async (component: V1Alpha1Component) => {
    try {
      const response = await api.dexter.get(
        `/blocks/${component.block_info.name}/?version=${component.block_info.version}`
      );
      // for some reason dexter is sending array insight response.data.data
      const data = response.data.data[0];
      return data; // Assuming the API returns a valid block structure
    } catch (error) {
      console.error(
        `Failed to fetch block data for ${component.block_info.name}:`,
        error
      );
      return undefined;
    }
  };

  const nodes = await Promise.all(
    spec.components.map(async (component: V1Alpha1Component) => {
      // NOTES: component.name will always start with the original blockName on Dexter
      // NOTE: blockCacheCopy has to be undefined if component version does not match
      // Only blockCacheCopy will be undefined, only when block version from workflow spec, does not
      // match that of the block from redux
      // When undefine, we will query the block by name and version, to dexter, to get the blockSpec for xxx version preset in the workflow spec
      let blockCacheCopy = blocks.find(
        (block: block) =>
          component.name.startsWith(block.spec.name || '') &&
          component.block_info.version === block.spec.version
      ) as block | undefined;

      // If blockCacheCopy is undefined, call the API as a fallback
      if (!blockCacheCopy) {
        blockCacheCopy = await fetchBlockData(component);
      }

      const count = component.name.split('-')?.[1] || '';
      const OptimisticComponentSpec = {
        name: component.name,
        block_id: component.block_id,
        block_info: component.block_info,
        properties: {
          position: {
            ...component.properties?.position
          }
        },
        depends_on: component.depends_on || null,
        inputs: [
          ...(component?.inputs?.map((input, i) => {
            const isArtifact =
              // @ts-expect-error is_artifact is part of block spec schema but not present in v1alpha1
              blockCacheCopy?.spec?.inputs[i]?.is_artifact ?? false;
            return {
              ...input,
              is_artifact: isArtifact
            };
          }) || [])
        ],
        outputs: [
          ...(blockCacheCopy?.spec.outputs.map(output => {
            return {
              name: output.name,
              // @ts-expect-error is_artifact is part of block spec schema but not present in v1alpha1
              is_artifact: output.is_artifact
            };
          }) || [])
        ]
      };

      const aoi_id =
        (blockCacheCopy?.spec.type === 'source' &&
          component?.inputs?.find(input => input.name === 'aoi_id')?.value) ||
        '';

      const newNode = {
        id: component.name,
        type: blockCacheCopy?.spec.type || '',
        data: {
          label:
            (aoi_id !== '' && aoi_id) ||
            `${blockCacheCopy?.spec?.title || ''} ${count}` ||
            '',
          blockSpec: { ...OptimisticComponentSpec }
        },
        position: { ...component?.properties?.position },
        style: { ...defaultNodeStyle }
      };

      return newNode;
    })
  );
  return nodes;
};

export const CreateCanvasEdges = (spec: V1Alpha1Spec) => {
  const Edges: Edge[] = [];
  spec.components.forEach((component: V1Alpha1Component) => {
    // ignore AOI blocks as AOI blocks only give output
    // and that a block input cannot have multiple connections
    // however a block output can have multiple connections
    if (!component.name.startsWith('aoi')) {
      component?.inputs?.forEach((input: V1Alpha1Input) => {
        // check first whether input has 'from' attribute
        // this means input has connection from another block
        if (_.has(input, 'from') && input.from !== null) {
          // construct edge object for ReactFlow
          const source = input?.from?.component || '';
          const sourceHandle = `${input?.from?.component}-output`;
          const target = component.name;
          const targetHandle = input.name;

          const ReactFLowEdgeId = createReactFlowEdgeId(
            source,
            sourceHandle,
            target,
            targetHandle
          );

          const newEdge = {
            id: ReactFLowEdgeId,
            source: source,
            sourceHandle: sourceHandle,
            target: target,
            targetHandle: targetHandle
          };
          Edges.push(newEdge);
        }
      });
    }
  });

  return Edges;
};

/**
 *
 * @param {properties} properties - properties for task/inference output
 * @returns
 */
export const isDiscrete = (properties: properties) => {
  if (_.has(properties, 'visualisation')) {
    if (properties?.visualisation?.type === COLORMAP_TYPES.discreteColormap)
      return true;
  }

  return false;
};

export function isInferenceDicrete(
  insights: V1Alpha1InferenceListResponseBody[],
  assetType: string,
  parameter_name: string
) {
  if (assetType === '') return false;

  if (MODELS_ALLOWED_CHANGE_DETECTION.includes(assetType)) {
    const modelWiseInsights = insights.filter(
      i => i?.metadata?.block_name === assetType
    );

    // get sample inference, since discrete classes are predefined from model
    const inferenceSample =
      (modelWiseInsights.length && modelWiseInsights[0]) ||
      ({} as V1Alpha1InferenceListResponseBody);

    // when inference has multi parameter output
    // we need extract sample output from the specific parameter only
    if (PARAMETER_MODELS.includes(assetType)) {
      const inferenceSampleOutput =
        inferenceSample?.output?.find(
          output => output.name === parameter_name
        ) || {};

      return isDiscrete(inferenceSampleOutput?.properties || {});
    }

    // For non parameter
    const inferenceSampleOutput =
      inferenceSample?.output?.find(
        output => output.format === INPUT_FORMATS.FormatRaster
      ) || {};

    return isDiscrete(inferenceSampleOutput?.properties || {});
  }
}

// get classes from discretization property of raster
export const GetDiscretizationClasses = (properties: properties) => {
  if (_.has(properties, 'discretization')) {
    if (properties?.discretization?.type === DISCRETISATION_TYPES.discrete) {
      const { classes = [] } = properties?.discretization || {};
      return classes;
    }
  }

  return [];
};

// get visualisation properties for continous data for a raster
export const GetContinousVisualisationProperties = (
  properties: properties
): { colormap_name: string; rescale: number[] } => {
  if (_.has(properties, 'visualisation')) {
    if (properties?.visualisation?.type === VISUALISATION_TYPES.continuous) {
      const { continuous = {} } = properties?.visualisation || {};
      const { color_map_name = '', bandwise_range = [{ min: 0, max: 0 }] } =
        continuous as continuous;
      return {
        colormap_name: color_map_name,
        rescale: [bandwise_range[0]?.min, bandwise_range[0]?.max]
      };
    }
  }

  return { colormap_name: '', rescale: [0, 0] };
};

export const getBucketVisualisationProperties = (
  properties: properties
): { buckets: bandwiseBucketItems[] } => {
  if (_.has(properties, 'visualisation')) {
    if (properties?.visualisation?.type === VISUALISATION_TYPES.bucket) {
      const buckets =
        properties?.visualisation?.bucket?.bandwise?.[0]?.items ?? [];

      return {
        buckets: buckets
      };
    }
  }

  return {
    buckets: []
  };
};

/**
 *
 * @param {V1Alpha1Output} output - raster output of task or inference
 * @returns {string} - JSON encoded custom discrete color map
 */
export const DiscreteColormapGenerator = (
  output: V1Alpha1Input & V1Alpha1Output
) => {
  if (_.has(output, 'properties')) {
    if (_.has(output.properties, 'visualisation')) {
      const visualisation =
        output.properties?.visualisation ?? ({} as visualisation);
      if (visualisation?.type === VISUALISATION_TYPES.discrete) {
        const colormapObj = visualisation.discrete;
        return encodeURIComponent(JSON.stringify(colormapObj));
      }
    }
  }

  return '';
};

export const getDiscreteCogURL = (
  output: V1Alpha1Input & V1Alpha1Output,
  format: string
) => {
  const s3URL = output?.value ?? '';
  const custom_colormap = DiscreteColormapGenerator(output);
  const custom_colormap_type = 'linear';

  const params = {
    url: s3URL,
    colormap: custom_colormap,
    colormap_type: custom_colormap_type
  };

  const tilerURL = `${
    import.meta.env.VITE_COG_TILER
  }/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?${queryString.stringify(
    params,
    {
      sort: false,
      strict: false,
      encode: false
    }
  )}`;

  return tilerURL;
};

export const getContinuousSingleBandCogURL = (
  output: V1Alpha1Input & V1Alpha1Output,
  format: string
) => {
  const s3URL = output?.value ?? '';
  const visualisation_properties = GetContinousVisualisationProperties(
    output?.properties
  );

  const { colormap_name = '', rescale = [0, 0] } = visualisation_properties;
  const params = {
    url: s3URL,
    colormap_name: colormap_name.toLowerCase(),
    rescale: rescale.join(`%2C`),
    resampling_method: RESAMPLING_METHODS.nearestResampling
  };

  const tilerURL = `${
    import.meta.env.VITE_COG_TILER
  }/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?${queryString.stringify(
    params,
    {
      sort: false,
      strict: false,
      encode: false
    }
  )}`;

  return tilerURL;
};

export const getBucketSingleBandCogURL = (
  output: V1Alpha1Input & V1Alpha1Output,
  format: string
) => {
  const s3URL = output?.value ?? '';
  const buckets = getBucketVisualisationProperties(output?.properties);

  const colormapArray = [
    ...buckets.buckets.map(bucket => {
      const rgbaValue = h2RGdal(bucket.color_code);
      const range = [bucket.min, bucket.max];
      const item = [
        [...range],
        [rgbaValue?.r, rgbaValue?.g, rgbaValue?.b, rgbaValue?.a]
      ];

      return item;
    })
  ];

  const params = {
    url: s3URL,
    colormap: encodeURIComponent(JSON.stringify(colormapArray))
  };

  const tilerURL = `${
    import.meta.env.VITE_COG_TILER
  }/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?${queryString.stringify(
    params,
    {
      sort: false,
      strict: false,
      encode: false
    }
  )}`;

  return tilerURL;
};

// returns TiTiler usable URL for all output.format === raster from dexter
export const getCogURLForOutput = (
  output: V1Alpha1Input & V1Alpha1Output,
  format: string = 'webp'
) => {
  const { type = '' } = output?.properties?.visualisation || {};

  // will return a TileLayer with discrete data
  if (type === VISUALISATION_TYPES.discrete) {
    const url = getDiscreteCogURL(output, format);
    return url;
  }

  if (type === VISUALISATION_TYPES.continuous) {
    const url = getContinuousSingleBandCogURL(output, format);
    return url;
  }

  if (type === VISUALISATION_TYPES.bucket) {
    const url = getBucketSingleBandCogURL(output, format);
    return url;
  }

  return '';
};

export function FetchDiscreteData(
  insights: V1Alpha1InferenceListResponseBody[],
  assetType: string,
  parameter_name: string
) {
  const modelWiseInsights = insights.filter(
    i => i?.metadata?.block_name === assetType
  );
  const inferenceSample =
    (modelWiseInsights.length && modelWiseInsights[0]) ||
    ({} as V1Alpha1InferenceListResponseBody);

  // when inference has multi parameter output
  // we need extract sample output from the specific parameter only
  if (PARAMETER_MODELS.includes(assetType)) {
    const inferenceSampleOutput =
      inferenceSample?.output?.find(output => output.name === parameter_name) ||
      {};

    return GetDiscretizationClasses(inferenceSampleOutput?.properties || {});
  }

  // For non parameter
  const inferenceSampleOutput =
    inferenceSample?.output?.find(
      output => output.format === INPUT_FORMATS.FormatRaster
    ) || {};

  return GetDiscretizationClasses(inferenceSampleOutput?.properties || {});
}

export const getRasterVisualisationColorLegends = (
  raster: V1Alpha1Input & V1Alpha1Output
) => {
  const { properties } = raster || {};
  const discreteClasses = GetDiscretizationClasses(properties);
  const continousData = GetContinousVisualisationProperties(properties);

  if (discreteClasses.length > 0) {
    const reducedColorBuckets = discreteClasses.reduce(
      (acc: { [key: string]: string }, value) => {
        acc[value.name] = value.color;
        return acc;
      },
      {}
    );

    return {
      title: '',
      type: COLORMAP_TYPES.discreteColormap,
      buckets: reducedColorBuckets
    };
  }

  if (!_.isEmpty(continousData) && continousData.colormap_name !== '')
    return {
      title: '',
      type: COLORMAP_TYPES.rangeColormap,
      // author may send colormap values in case which is not supported by riotiler
      colormap: continousData.colormap_name.toLowerCase(),
      rescale: [...continousData.rescale]
    };

  return undefined;
};
